import React, { useState } from 'react';
import { Card, Modal, Form, Button, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers,
  faDownload,
  faFilter,
  faTimes,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db, auth } from '../../services/firebase';
import DatePicker from 'react-datepicker';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import enUS from 'date-fns/locale/en-US';
import itIT from 'date-fns/locale/it';
import zhCN from 'date-fns/locale/zh-CN';
import "react-datepicker/dist/react-datepicker.css";

// Registra le localizzazioni
registerLocale('en', enUS);
registerLocale('it', itIT);
registerLocale('zh', zhCN);

const ExportClientsTool = () => {
  const { t, i18n } = useTranslation();
  const [showExportModal, setShowExportModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [filters, setFilters] = useState({
    brand: '',
    model: '',
    dateFrom: null,
    dateTo: null
  });

  // Aggiorna il locale in base alla lingua corrente
  const currentLocale = (() => {
    switch (i18n.language) {
      case 'it': return itIT;
      case 'zh': return zhCN;
      default: return enUS;
    }
  })();

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (date, name) => {
    setFilters(prev => ({
      ...prev,
      [name]: date
    }));
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error(t('tools.exportClients.errors.notAuthenticated'));

      // Costruisci la query base
      const devicesRef = collection(db, `users/${userId}/devices`);
      let constraints = [];

      // Aggiungi i filtri per marca e modello
      if (filters.brand) {
        constraints.push(where('brand', '==', filters.brand));
      }
      if (filters.model) {
        constraints.push(where('model', '==', filters.model));
      }

      // Aggiungi il filtro per la data
      if (filters.dateFrom || filters.dateTo) {
        if (filters.dateFrom) {
          constraints.push(where('createdAt', '>=', Timestamp.fromDate(filters.dateFrom)));
        }
        if (filters.dateTo) {
          // Imposta la data fine alla fine della giornata
          const endDate = new Date(filters.dateTo);
          endDate.setHours(23, 59, 59, 999);
          constraints.push(where('createdAt', '<=', Timestamp.fromDate(endDate)));
        }
      }

      // Aggiungi l'ordinamento per data
      constraints.push(orderBy('createdAt', 'desc'));

      // Crea la query con tutti i vincoli
      const q = query(devicesRef, ...constraints);

      // Esegui la query
      const querySnapshot = await getDocs(q);
      const devices = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filtra i dispositivi che hanno un numero di telefono
      const devicesWithPhone = devices.filter(device => device.customerPhone);

      if (devicesWithPhone.length === 0) {
        throw new Error(t('tools.exportClients.errors.noPhoneNumbers'));
      }

      // Prepara i dati per l'esportazione
      const exportData = devicesWithPhone.map(device => ({
        numero: device.customerPhone,
        marca: device.brand,
        modello: device.model,
        data: device.createdAt?.toDate().toLocaleDateString() || 'N/A'
      }));

      // Crea il file CSV
      const headers = [
        t('tools.exportClients.csvHeaders.number'),
        t('tools.exportClients.csvHeaders.brand'),
        t('tools.exportClients.csvHeaders.model'),
        t('tools.exportClients.csvHeaders.date')
      ];
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => [
          row.numero,
          row.marca,
          row.modello,
          row.data
        ].join(','))
      ].join('\n');

      // Crea e scarica il file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `numeri_clienti_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess(true);
      setShowExportModal(false);
    } catch (error) {
      console.error(t('tools.exportClients.errors.exportError'), error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card className="h-100">
        <Card.Header className="bg-light">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
            <h5 className="mb-0">{t('tools.exportClients.title')}</h5>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="d-flex flex-column h-100">
            <div className="tool-icon mb-3">
              <FontAwesomeIcon icon={faUsers} />
            </div>
            <p className="text-muted mb-3">
              {t('tools.exportClients.description')}
            </p>
            <div className="mt-auto">
              <Button 
                variant="primary" 
                className="w-100"
                onClick={() => setShowExportModal(true)}
              >
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                {t('tools.exportClients.button')}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Modal per l'esportazione */}
      <Modal 
        show={showExportModal} 
        onHide={() => setShowExportModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
            {t('tools.exportClients.modalTitle')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}
          <Form>
            <div className="row g-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>
                    <FontAwesomeIcon icon={faFilter} className="me-2" />
                    {t('tools.exportClients.filters.brand')}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="brand"
                    value={filters.brand}
                    onChange={handleFilterChange}
                    placeholder={t('tools.exportClients.filters.brandPlaceholder')}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>
                    <FontAwesomeIcon icon={faFilter} className="me-2" />
                    {t('tools.exportClients.filters.model')}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="model"
                    value={filters.model}
                    onChange={handleFilterChange}
                    placeholder={t('tools.exportClients.filters.modelPlaceholder')}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>
                    <FontAwesomeIcon icon={faFilter} className="me-2" />
                    {t('tools.exportClients.filters.dateFrom')}
                  </Form.Label>
                  <DatePicker
                    selected={filters.dateFrom}
                    onChange={(date) => handleDateChange(date, 'dateFrom')}
                    locale={currentLocale}
                    dateFormat="P"
                    className="form-control"
                    placeholderText={t('tools.exportClients.filters.dateFrom')}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>
                    <FontAwesomeIcon icon={faFilter} className="me-2" />
                    {t('tools.exportClients.filters.dateTo')}
                  </Form.Label>
                  <DatePicker
                    selected={filters.dateTo}
                    onChange={(date) => handleDateChange(date, 'dateTo')}
                    locale={currentLocale}
                    dateFormat="P"
                    className="form-control"
                    placeholderText={t('tools.exportClients.filters.dateTo')}
                  />
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowExportModal(false)}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            {t('common.cancel')}
          </Button>
          <Button 
            variant="primary" 
            onClick={handleExport}
            disabled={loading}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                {t('tools.exportClients.exporting')}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                {t('tools.exportClients.export')}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ExportClientsTool; 